import { useEffect, useState, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import ScrollToTop from "@/components/ScrollToTop";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import GiftVoucherWidget from "@/components/GiftVoucherWidget";
import { apiSync } from "@/lib/api";
import { DESKTOP_MIN_WIDTH, TABLET_MIN_WIDTH, getNostiumButton } from "@/lib/nostiumButton";
import Index from "./pages/Index";

// Lazy load non-critical UI components to reduce initial bundle
const WalkingAlpaca = lazy(() => import("@/components/WalkingAlpaca"));

// Lazy load non-critical pages to reduce initial bundle size
const Activities = lazy(() => import("./pages/Activities"));
const Alpacas = lazy(() => import("./pages/Alpacas"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Contact = lazy(() => import("./pages/Contact"));
const About = lazy(() => import("./pages/About"));
const Collaborations = lazy(() => import("./pages/Collaborations"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const AppContent = () => {
  const settings = apiSync.getSiteSettings();
  const [alpacaEnabled] = useState(() => settings?.walkingAlpacaEnabled !== false);
  const [alpacaMobileDisabled] = useState(() => settings?.walkingAlpacaMobileDisabled === true);
  const location = useLocation();

  useEffect(() => {
    const DESKTOP_LABEL = "Maak je reservering";
    const MOBILE_LABEL = "Reserveer";

    let rafId = 0;
    const updateWidgetButton = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const widgetButton = getNostiumButton();
        if (!widgetButton) return;

        const isAdmin = window.location.pathname.startsWith("/admin");
        if (isAdmin) {
          widgetButton.style.setProperty("display", "none", "important");
          return;
        } else {
          widgetButton.style.removeProperty("display");
        }

        const viewportWidth = window.innerWidth;
        const isMobile = viewportWidth < TABLET_MIN_WIDTH;
        const isTablet = viewportWidth >= TABLET_MIN_WIDTH && viewportWidth < DESKTOP_MIN_WIDTH;

        widgetButton.textContent = isMobile ? MOBILE_LABEL : DESKTOP_LABEL;
        widgetButton.style.setProperty("transform", "translate(1px, 1px)", "important");

        if (isTablet) {
          widgetButton.style.setProperty("padding", "6px 10px", "important");
          widgetButton.style.setProperty("font-size", "12px", "important");
          widgetButton.style.setProperty("line-height", "1.1", "important");
          widgetButton.style.setProperty("min-height", "32px", "important");
        } else {
          widgetButton.style.removeProperty("padding");
          widgetButton.style.removeProperty("font-size");
          widgetButton.style.removeProperty("line-height");
          widgetButton.style.removeProperty("min-height");
        }
      });
    };

    const observer = new MutationObserver(() => {
      updateWidgetButton();
      // Ensure nostium-widget div keeps its aria-label
      const widget = document.getElementById("nostium-widget");
      if (widget && !widget.getAttribute("aria-label")) {
        widget.setAttribute("aria-label", "Maak je reservering");
      }
    });

    observer.observe(document.body, { childList: true });
    window.addEventListener("resize", updateWidgetButton, { passive: true });
    updateWidgetButton();

    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
      window.removeEventListener("resize", updateWidgetButton);
    };
  }, [location.pathname]);

  const isAdminPage = location.pathname.startsWith("/admin");
  const showAlpaca = alpacaEnabled && location.pathname === "/";

  return (
    <>
      <ScrollToTop />
      {!isAdminPage && <LanguageSwitcher />}
      {!isAdminPage && <GiftVoucherWidget />}
      {showAlpaca && !alpacaMobileDisabled && <WalkingAlpaca />}
      {showAlpaca && alpacaMobileDisabled && (
        <div className="hidden md:block">
          <WalkingAlpaca />
        </div>
      )}
      <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/activiteiten" element={<Activities />} />
          <Route path="/alpacas" element={<Alpacas />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/over-ons" element={<About />} />
          <Route path="/samenwerkingen" element={<Collaborations />} />
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AppContent />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
